import Env from '@env';

(function(DocumentFragment, Element, Node) {
    "use strict";

    const df = document.createDocumentFragment();
    df.appendChild(document.createElement('div'));
    if(!df.firstElementChild) {
        if(!DocumentFragment.prototype.firstElementChild) {
            Object.defineProperty(DocumentFragment.prototype, 'firstElementChild', {
                get: function() {
                    let e = this.firstChild;
                    while(e && e.nodeType !== Node.ELEMENT_NODE)
                        e = e.nextSibling;
                    return e;
                }
            });
            Object.defineProperty(DocumentFragment.prototype, 'lastElementChild', {
                get: function() {
                    let e = this.lastChild;
                    while(e && e.nodeType !== Node.ELEMENT_NODE)
                        e = e.previousSibling;
                    return e;
                }
            });
        }
    }
})(DocumentFragment, Element, Node);

import { isAbortOrCancelledError } from '@core/helpers/errorHelper';

import { createLogger } from '@core/services/logger.service';
import { getDefaultLocale } from '@core/services/locale';

const log = createLogger('index');

// link automation module with the page
import * as Automation from '@ui-core/automation';
Automation.addLookInsideElement('ui-page, ' +
    'ui-login-view, ' +
    'ui-page-header, ' +
    'ui-alert-default, ' +
    'ui-logged-user, ' +
    'ui-billing-form, ' +
    'ui-stripe-form, ' +
    'rx-ui-task, ' +
    'zoom-layer, ' +
    'scroll-layer, ' +
    'rx-ui-form, ' +
    'ui-form, ' +
    'ui-form-section-default,' +
    ' ui-empty-content,' +
    ' ui-dropdown, ' +
    'ui-visit-cell, ' +
    'user-interaction-controller, ' +
    'ui-confirmation-popup,' +
    ' ui-dot-color, ' +
    'ui-role-selector-default,' +
    'ui-action-overlay,' +
    'ui-issue-attachment,' +
    'ui-toggle-default,' +
    'ui-bug-note-add,' +
    'ui-shared-report-info,' +
    'ui-form-attachment-input,' +
    'ui-bug-note-editor,' +
    'ui-remark-dropdown-editable,' +
    'rx-ui-form-type-popup,' +
    'rx-ui-form-page-create-popup,' +
    'ui-form-field-row,' +
    'ui-font-display,' +
    'ui-date-display,' +
    'rx-ui-table,' +
    'ui-password-confirmation-popup,' +
    'ui-project-list-cell,' +
    'ui-bug-list-cell,' +
    'ui-bug-form-history,' +
    'ui-header-back-button,' +
    'ui-popup-close-button,' +
    'orm-main-context'
    );

window['automation'] = Automation;

import * as Sentry from "@sentry/browser";
import OfflineServiceInstance from "@archipad/services/offline.service";
import { watchNetworkAndPageLifecycle } from "@ui-archipad/logs/lifecycle-logs";
import { registerArchipadContextPlugin } from "@ui-app/logs/archipad-context.plugin";
import { ignoredErrors, isFromBrowserExtension } from "@ui-app/logs/sentry-filters";

registerArchipadContextPlugin();

const excludedIntegrations = [
    'GlobalHandlers',
    'BrowserApiErrors', // previously named TryCatch
];

Sentry.init({
    dsn: Env.sentry?.dsn,
    // production | staging | preview | local, local is disabled unless built with `--enableSentry`
    environment: Env.sentry?.environment,
    enabled: Env.sentry?.enabled ?? false,
    release: Env.sentry?.release,
    // Only the structured logs (LogLayer, see @archipad/logs/logger), console logs are not forwarded
    enableLogs: true,
    // Local builds only (see getSentryConfig): lets a developer follow what happened around an error
    // (page load, navigations, fetch/XHR spans) and links the logs and errors of a trace together
    tracesSampleRate: Env.sentry?.tracesSampleRate ?? 0,
    // Keep the events and logs in IndexedDB while offline and send them once back online
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    ignoreErrors: ignoredErrors,
    // Drop abort/cancellation errors here too: our own logger already ignores them, but Sentry's
    // unhandledrejection auto-capture (GlobalHandlers) can still report them independently.
    beforeSend(event, hint) {
        if (isAbortOrCancelledError(hint?.originalException)) {
            return null;
        }
        if (isFromBrowserExtension(event)) {
            return null;
        }
        return event;
    },
    /** @see https://docs.sentry.io/platforms/javascript/integrations/default/ */
    integrations(defaultIntegrations) {
        const integrations = defaultIntegrations.filter((integration) => {
            const isExcluded = excludedIntegrations.includes(integration.name);
            return !isExcluded;
        });

        if (Env.sentry?.tracesSampleRate) {
            integrations.push(Sentry.browserTracingIntegration());
        }

        console.log(integrations);
        return integrations;
    },
});

watchNetworkAndPageLifecycle(OfflineServiceInstance.onlineStatus$);

import '@ui-archipad/helpers/commands';

import { installGtag } from './gtag';

if (Env.GTAG_ID) {
    installGtag(Env.GTAG_ID);
}

window.Stripe = null;
window.filestack = null;


const _supportedLocales = ['en', 'fr', 'de', 'es'];


const FEATURE_TEST_DATABASE = 'archipad-feature-test';

/**
 * Checks the features the app cannot run without. The supported browsers have them all, except when storage is
 * disabled or IndexedDB cannot open a database (private navigation on some browsers), which the "not supported"
 * page explains.
 */
async function hasRequiredFeatures(): Promise<boolean> {
    try {
        if (!window.indexedDB || typeof IDBObjectStore.prototype.openKeyCursor !== 'function' || !window.customElements || !window.localStorage || !window.sessionStorage) {
            return false;
        }
    } catch {
        // reading window.localStorage throws when the storage is disabled
        return false;
    }

    // the only reliable test of IndexedDB is to open a database: a browser may expose the API and then refuse
    return new Promise((resolve) => {
        const request = window.indexedDB.open(FEATURE_TEST_DATABASE);
        request.onsuccess = () => {
            request.result.close();
            window.indexedDB.deleteDatabase(FEATURE_TEST_DATABASE);
            resolve(true);
        };
        request.onerror = () => resolve(false);
        request.onblocked = () => resolve(true);
    });
}

async function init(){
    if ( window.location.search.indexOf('debug-notsupported') !== -1 ) {
        console.log("not supported debug");
        return showPage(false);
    } else {
        return showPage(await hasRequiredFeatures());
    }
}

init().catch((err) => {
    throw err;
});


function showPage(hasFeatures) {
    const notSupportedDiv = document.getElementById('browserNotSupported');
    const locale = getDefaultLocale()?.baseName;

    const ua = window.navigator.userAgent;
    const IS_MSIE = ua.indexOf("Trident/") !== -1;

    if ( ! hasFeatures || IS_MSIE ) {
        notSupportedDiv.setAttribute('class', 'notsupported '+ locale);

        // TODO XXX : What is this beast ? (SSL cert broken, not found at the end.)
        const search = '#/shared-report/';
        if ( window.location.hash.indexOf(search) === 0 ) {
            const reportId = window.location.hash.substr(search.length);

            const to = locale == 'fr' ? "https://v1.archipad.com/fr/rapport-partage.html?report=" : "https://v1.archipad.com/en/shared-report.html?report=";

            window.location.href = to + reportId;
        }

    } else {
        const loadingDiv = document.getElementById('app-loading');
        loadingDiv.setAttribute('class', 'loading '+ locale);

        document.body.addEventListener('app-ready', function() {
            loadingDiv.classList.add("loaded");
            setTimeout(function() {
                loadingDiv.style.display = 'none';
            }, 1000);
        });

        import("./bootstrap.html")
            .then(function() {
                const appBootstrap = document.createElement('app-bootstrap');
                appBootstrap.innerHTML = `
                    <template slot="template">
                        <archipad-app id="app"></archipad-app>
                    </template>`;
                notSupportedDiv.parentNode.appendChild(appBootstrap);
            })
            .catch(function(err) {
                if (err?.name === "ChunkLoadError"){
                    window.location.reload();
                }
                console.log('Error while loading bootstrap', err);
                const archipadCloudAppNode = document.createElement('div');
                archipadCloudAppNode.innerText = err;
                notSupportedDiv.parentNode.appendChild(archipadCloudAppNode);
                loadingDiv.classList.add("loaded");
            });

        loadingDiv.style.display = 'block';
    }
}
